import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState, useEffect, useMemo, useCallback } from "react";
import { toast } from "sonner";
import { Controller, useForm } from "react-hook-form";

import { SidebarInset } from "@/components/ui/sidebar";
import { DashboardHeader } from "@/components/dashboard-header";
import { PaginationComponent } from "@/components/crud/Pagination/Pagination";
import { usePaginationState } from "@/hooks/ui/usePaginationState";

import useFetchShifts from "@/hooks/api/shift/useFetchShift";
import useFetchTimeSheet from "@/hooks/api/timesheet/useFetchTimeSheets";
import useAutoCompleteUser from "@/hooks/api/user/useAutoCompleteUser";
import { TimeSheetTable } from "@/components/crud/table/TimeSheetTable";
import { SelectFilter } from "@/components/crud/filters/SelectFilter";
import { MultiSelectFilter } from "@/components/crud/filters/MultiSelectFilter";
import { DateSelector } from "@/components/crud/filters/DateSelector";
import {
  timeSheetStatusFilterOptions,
  type TimeSheetStatusFilter,
} from "@/lib/timesheet-status-filter";
import PageHeader from "@/components/reusable-components/header/page-header";
import FiltersPanel from "@/components/reusable-components/header/filter-pannel";
import useExportTimeSheets from "@/hooks/api/timesheet/useExportTimeSheets";
import { ExportTimeSheetModal } from "@/components/crud/modals/ExportTimeSheetModal";
import { FileDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { DirectAdjustmentDialog } from "@/components/crud/modals/DirectAdjustmentDialog";
import SelectUser from "@/components/reusable-components/select-user";
import type { DateRange } from "react-day-picker";

import type { SortDirection } from "@/components/reusable-components/sort-arrow";
import { useDataTableOrdering } from "@/hooks/ui/useDataTableOrdering";
import type { TimeSheetOrdering } from "@/hooks/api/timesheet/useFetchTimeSheets";
import { nowInOrgTimezone, dayjs } from "@/lib/date-utils";
import { PAGE_SIZE_OPTIONS } from "@/lib/constants";
import { useMyPermissions } from "@/hooks/api/authorization/useAuthorization";
import { can } from "@/lib/permissions";

export const Route = createFileRoute("/admin/_layout/attendance/timesheets")({
  component: RouteComponent,
});


type OffShiftFilter = "all" | "yes" | "no";
const OFF_SHIFT_OPTIONS: { label: string; value: OffShiftFilter }[] = [
  { label: "All check-ins", value: "all" },
  { label: "Off-shift check-in", value: "yes" },
  { label: "No off-shift check-in", value: "no" },
];

function RouteComponent() {
  return <TimeSheetDataTable />;
}

function TimeSheetDataTable() {
  const navigate = useNavigate();

  const { data: capabilities } = useMyPermissions();
  const canManage = can(capabilities, "attendance.timesheet.adjust");

  // Fetch hooks
  const shiftsQuery = useFetchShifts({ page_size: 100 });
  const shifts = shiftsQuery.data?.results ?? [];

  const [autoCompleteSearch, setAutoCompleteSearch] = useState("");
  const autoCompleteQuery = useAutoCompleteUser({
    search: autoCompleteSearch || undefined,
  });
  const pageUsers = useMemo(
    () => autoCompleteQuery.data?.pages.flatMap((p) => p.results) ?? [],
    [autoCompleteQuery.data]
  );
  const autoCompleteLoading = autoCompleteQuery.isFetching;

  const { ordering, toggleOrdering, getApiOrdering, setOrdering } =
    useDataTableOrdering<
      {
        date: SortDirection;
        created_at: SortDirection;
        expected_check_in: SortDirection;
      },
      TimeSheetOrdering
    >({
      date: null,
      created_at: null,
      expected_check_in: null,
    });
  const [dateRange, setDateRange] = useState<DateRange | undefined>({
    from: nowInOrgTimezone().startOf("day").toDate(),
    to: nowInOrgTimezone().toDate(),
  });
  const [shiftFilter, setShiftFilter] = useState<string | undefined>();
  const [statusFilter, setStatusFilter] = useState<TimeSheetStatusFilter[]>([]);
  const [offShiftFilter, setOffShiftFilter] = useState<OffShiftFilter>("all");
  // The picker's own search box. It only searches the user autocomplete --
  // the table filters on the picked user's id (T-167).
  const [userPickerSearch, setUserPickerSearch] = useState("");

  // React Hook Form for user filter
  const { setValue, watch, control } = useForm<{ user_id?: number }>({
    defaultValues: { user_id: undefined },
  });

  const watchedUserId = watch("user_id");

  // Keep the picked user even after the autocomplete page no longer contains
  // them (closing the picker resets its search), so the label stays right.
  const [pickedUser, setPickedUser] = useState<(typeof pageUsers)[number]>();
  useEffect(() => {
    if (watchedUserId === undefined) {
      setPickedUser(undefined);
      return;
    }
    const found = pageUsers.find((u) => u.id === watchedUserId);
    if (found) setPickedUser(found);
  }, [watchedUserId, pageUsers]);
  const users = useMemo(
    () =>
      pickedUser && !pageUsers.some((u) => u.id === pickedUser.id)
        ? [pickedUser, ...pageUsers]
        : pageUsers,
    [pickedUser, pageUsers]
  );

  const { page, setPage, pageSize, handlePageSizeChange } =
    usePaginationState();

  const exportTimeSheetsMutation = useExportTimeSheets();

  const timeSheetsQuery = useFetchTimeSheet({
    page,
    page_size: pageSize,
    ordering: getApiOrdering(),
    start_date: dateRange?.from
      ? dayjs(dateRange.from).format("YYYY-MM-DD")
      : undefined,
    end_date: dateRange?.to
      ? dayjs(dateRange.to).format("YYYY-MM-DD")
      : undefined,
    shift_assignment__shift: shiftFilter,
    user: watchedUserId !== undefined ? String(watchedUserId) : undefined,
    status: statusFilter.length ? statusFilter : undefined,
    off_shift: offShiftFilter === "all" ? undefined : offShiftFilter === "yes",
  });
  const timeSheets = timeSheetsQuery.data?.results ?? [];
  const count = timeSheetsQuery.data?.count ?? 0;

  const isMutating = exportTimeSheetsMutation.isPending;
  const isLoading =
    timeSheetsQuery.isPending || shiftsQuery.isPending || isMutating;
  const isFetching =
    timeSheetsQuery.isFetching || shiftsQuery.isFetching || isMutating;

  // Back to page 1 whenever a filter changes
  useEffect(() => {
    setPage(1);
  }, [
    ordering,
    watchedUserId,
    dateRange,
    shiftFilter,
    statusFilter,
    offShiftFilter,
    setPage,
  ]);

  const totalPages = useMemo(() => {
    return count ? Math.ceil(count / pageSize) : 1;
  }, [count, pageSize]);

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setPage(newPage);
    }
  };

  // For resetting after create, update and delete
  const resetFilters = () => {
    setOrdering({
      date: null,
      created_at: null,
      expected_check_in: null,
    });
    setDateRange(undefined);
    setShiftFilter(undefined);
    setStatusFilter([]);
    setUserPickerSearch("");
    setValue("user_id", undefined);
  };

  //autoCompleteUsers code
  const handleSearchUsers = useCallback((query: string) => {
    setAutoCompleteSearch(query);
  }, []);

  const [showFilters, setShowFilters] = useState(false);
  const [isCreateAdjustmentOpen, setIsCreateAdjustmentOpen] = useState(false);

  const [showExportModal, setShowExportModal] = useState(false);

  const openExportModal = () => {
    setShowExportModal(true);
  };

  const handleExport = (params: {
    date?: string;
    start_date?: string;
    end_date?: string;
    shift_assignment__shift?: string;
    user?: number;
    filename?: string;
  }) => {
    exportTimeSheetsMutation.mutate(
      {
        ...params,
        user: params.user !== undefined ? String(params.user) : undefined,
      },
      {
        onSuccess: () => {
          setShowExportModal(false);
          toast.success("Timesheets exported successfully");
        },
      }
    );
  };

  return (
    <SidebarInset>
      <DashboardHeader />

      <div className="space-y-4 p-6 pt-0">
        <PageHeader
          title="Timesheets"
          description="View and track employee work hours, shifts, and attendance
            records."
          onToggleFilter={() => setShowFilters((prev) => !prev)}
          onAdd={canManage ? () => setIsCreateAdjustmentOpen(true) : undefined}
          addLabel="Create Adjustment"
          isLoading={isFetching}
        >
          <Button
            onClick={openExportModal}
            className="flex items-center gap-2"
            disabled={isFetching}
            variant="outline"
          >
            <FileDown className="h-4 w-4" />
            Export
          </Button>
        </PageHeader>

        <FiltersPanel
          show={showFilters}
          onClear={resetFilters}
          isLoading={isFetching}
        >
          {/* User Filter with Autocomplete */}
          <Controller
            name="user_id"
            control={control}
            render={() => (
              <SelectUser
                watchedUserId={watchedUserId}
                users={users}
                onSearchUsers={handleSearchUsers}
                searchQuery={userPickerSearch}
                setSearchQuery={setUserPickerSearch}
                setValue={setValue}
                isLoading={autoCompleteLoading}
                fetchNextPage={autoCompleteQuery.fetchNextPage}
                hasMore={autoCompleteQuery.hasNextPage}
              />
            )}
          />

          {/* Shift Filter */}
          <SelectFilter
            placeholder="Filter by shift"
            options={shifts.map((shift) => {
              return { label: shift.name, value: String(shift.id) };
            })}
            value={shiftFilter}
            onChange={(value) => setShiftFilter(value)}
          />

          {/* Status Filter */}
          <MultiSelectFilter
            placeholder="Filter by status"
            options={timeSheetStatusFilterOptions}
            values={statusFilter}
            onChange={setStatusFilter}
          />

          {/* Off-shift check-in Filter (T-004) */}
          <SelectFilter
            placeholder="Off-shift check-ins"
            options={OFF_SHIFT_OPTIONS}
            value={offShiftFilter}
            onChange={setOffShiftFilter}
          />

          {/* Date Range Filter */}
          <DateSelector
            value={dateRange}
            onApply={(range) => setDateRange(range)}
            onClear={() => setDateRange(undefined)}
            label="Filter by date range"
          />
        </FiltersPanel>

        {/* Timesheet Table */}
        <TimeSheetTable
          timesheets={timeSheets}
          isLoading={isLoading}
          ordering={ordering}
          onOrderingChange={toggleOrdering}
          onRefresh={() => {
            timeSheetsQuery.refetch();
          }}
          onRowClick={(ts) =>
            navigate({
              to: "/admin/attendance/timesheets/$userId",
              params: { userId: String(ts.user.id) },
            })
          }
        />

        <PaginationComponent
          count={count}
          page={page}
          pageSize={pageSize}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          pageSizeOptions={PAGE_SIZE_OPTIONS}
          isLoading={isFetching}
        />

        <ExportTimeSheetModal
          isOpen={showExportModal}
          onClose={() => setShowExportModal(false)}
          onExport={handleExport}
          shifts={shifts}
          loading={exportTimeSheetsMutation.isPending}
          users={users}
          onSearchUsers={handleSearchUsers}
          autoCompleteLoading={autoCompleteLoading}
          initialDateRange={dateRange}
          initialUser={users.find((u) => u.id === watchedUserId)}
          initialShiftId={shiftFilter}
        />

        <DirectAdjustmentDialog
          isOpen={isCreateAdjustmentOpen}
          onClose={() => setIsCreateAdjustmentOpen(false)}
          onSuccess={() => timeSheetsQuery.refetch()}
          allowUserDateSelection
          title="Create Adjustment"
        />
      </div>
    </SidebarInset>
  );
}
